import { ApiClient } from "api";
import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { getErrorMessage } from "store/helper";
import { crudService } from "services/crud.service";
import { initialLoginUserState } from "store/constants";
import { NotificationManager } from "react-notifications";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

const { userEndPoint, userProfileEndPoint } = endPoints;

export const fetchLoginUserDetail = createAsyncThunk(
  "loginUser/fetchLoginUserDetail",
  async ({ userId }: { userId: string }, { rejectWithValue }) => {
    try {
      //   console.log({ userId });

      const response: AxiosResponse = await crudService.getById(
        userEndPoint,
        userId
      );
      //   console.log({ response });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  "users/fetchUserProfile",
  async ({ userId }: { userId: string }, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.getAll(
        `${userProfileEndPoint}?user__id=${userId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "users/updateUserProfile",
  async ({ formData, profileId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await ApiClient.put(
        `${userProfileEndPoint}/${profileId}/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 204) {
        NotificationManager.success("User profile updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage, "", 10000);
      return rejectWithValue(error.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  "loginUser/logoutUser",
  async (userDetail: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${userEndPoint}/logout/`,
        userDetail
      );

      NotificationManager.success(
        "Your session has ended safely.",
        "Logout complete"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const loginUserDetailSlice = createSlice({
  name: "loginUser",
  initialState: initialLoginUserState,
  reducers: {
    logout: (state) => {
      state.userDetail = null;
      state.isAuthenticated = false;
    },
  },

  extraReducers: (builder) => {
    // fetch login user detail
    builder
      .addCase(fetchLoginUserDetail.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchLoginUserDetail.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.userDetail = action.payload;
          state.isAuthenticated = true;
          state.isAdmin = !!(action.payload?.role === "ADMIN");
          state.isManager = !!(action.payload?.role === "MANAGER");
        }
      )
      .addCase(
        fetchLoginUserDetail.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    //   user profile for login user
    builder
      .addCase(fetchUserProfile.pending, (state: any) => {
        state.profileLoading = true;
      })
      .addCase(
        fetchUserProfile.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.profileLoading = false;
          state.userProfile = action?.payload?.objects[0] || {};
        }
      )
      .addCase(
        fetchUserProfile.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.profileLoading = false;
          state.error = action.payload as string;
          state.userProfile = null;
        }
      );

    //   update user profile
    builder
      .addCase(updateUserProfile.pending, (state: any) => {
        state.profileLoading = true;
      })
      .addCase(
        updateUserProfile.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.profileLoading = false;
        }
      )
      .addCase(
        updateUserProfile.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.profileLoading = false;
          state.error = action.payload as string;
        }
      );

    // logout user
    builder
      .addCase(logoutUser.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        logoutUser.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        logoutUser.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const userDetailReducer = loginUserDetailSlice.reducer;
export const { logout } = loginUserDetailSlice.actions;
